<template>
  <div class="componentRoot">
    <ButtonRowVue>
      <template v-slot:leftSide>
        <v-col :cols="searchBarCols" class="pr-2">
          <SearchBarVue :labelArray="['basics.name', 'basics.email']" @search="updateSearchQueue" />
        </v-col>
        <v-col cols="auto" class="pl-0">
          <UserListFilterVue @filter="applyFilter" />
        </v-col>
      </template>
      <v-col cols="auto" v-if="$checkRouterPermission('POST')">
        <v-btn @click="showEditUser($event)" class="primaryButton squareButton" flat><v-icon
            size="x-large">mdi-plus</v-icon></v-btn>
      </v-col>
    </ButtonRowVue>

    <TableVue :numPages="numPages" @switchPage="switchPage" id="userTable" :page="currPage" :total="totalCount"
      :resetPage="resetPage">
      <thead>
        <tr>
          <th class="text-left" style="width: 64px">
            
          </th>
          <th class="text-left" @click="switchSortBy('last_name')" :style="{
            color: sortBy == 'last_name' ? 'rgb(var(--v-theme-primary))' : 'unset',
          }">
            <div style="display: flex; align-items: center">
              <b style="font-size: medium">{{ $t("basics.name") }}</b>

              <div v-if="sortBy == 'last_name'">
                <v-icon v-if="!sortDirection">mdi-menu-up</v-icon>
                <v-icon v-else>mdi-menu-down</v-icon>
              </div>
              <div v-else>
                <v-icon>mdi-menu-right</v-icon>
              </div>
            </div>
          </th>
          <th class="text-left" @click="switchSortBy('first_name')" :style="{
            color: sortBy == 'first_name' ? 'rgb(var(--v-theme-primary))' : 'unset',
          }">
            <div style="display: flex; align-items: center">
              <b style="font-size: medium">{{ $t("basics.firstName") }}</b>

              <div v-if="sortBy == 'first_name'">
                <v-icon v-if="!sortDirection">mdi-menu-up</v-icon>
                <v-icon v-else>mdi-menu-down</v-icon>
              </div>
              <div v-else>
                <v-icon>mdi-menu-right</v-icon>
              </div>
            </div>
          </th>
          <th v-if="!isMobile" class="text-left" @click="switchSortBy('email')" :style="{
            color: sortBy == 'email' ? 'rgb(var(--v-theme-primary))' : 'unset',
          }">
            <div style="display: flex; align-items: center">
              <b style="font-size: medium">{{ $t("basics.email") }}</b>

              <div v-if="sortBy == 'email'">
                <v-icon v-if="!sortDirection">mdi-menu-up</v-icon>
                <v-icon v-else>mdi-menu-down</v-icon>
              </div>
              <div v-else>
                <v-icon>mdi-menu-right</v-icon>
              </div>
            </div>
          </th>
          <th v-if="!isMobile" class="text-left" :style="{
            color: 'unset',
          }">
            <div style="display: flex; align-items: center">
              <b style="font-size: medium">{{ $t("basics.permissions") }}</b>
            </div>
          </th>
          <th v-if="!isMobile" class="text-left" @click="switchSortBy('last_login')" :style="{
            color: sortBy == 'last_login' ? 'rgb(var(--v-theme-primary))' : 'unset',
          }">
            <div style="display: flex; align-items: center">
              <b style="font-size: medium">{{ $t("basics.lastLogin") }}</b>

              <div v-if="sortBy == 'last_login'">
                <v-icon v-if="!sortDirection">mdi-menu-up</v-icon>
                <v-icon v-else>mdi-menu-down</v-icon>
              </div>
              <div v-else>
                <v-icon>mdi-menu-right</v-icon>
              </div>
            </div>
          </th>
          <th style="width: 80px"></th>
        </tr>
      </thead>
      <tbody v-for="user in users" @click="gotoUser($event, user)" @mousedown.middle="gotoUserInNewTab($event, user)"
          :key="user.id" :style="{
            opacity: user.is_registered ? 1 : 0.7,
            'background-color': user.deleted ? 'rgb(var(--v-theme-background))' : '',
          }">
          <tr v-if="user.is_company_account" class="removeBorderBottom">
            <td style="height: 16px !important"></td>
            <td colspan="100"  style="height: 16px !important"><small><i>{{user.company_name}}</i></small></td>
          </tr>
        <tr>
          <td>
            <v-tooltip v-if="user.deleted" :text="$t('messages.userInvalid')">
              <template v-slot:activator="{ props }">
                <span v-bind="props">
                  <v-icon color="error" :style="{ opacity: 1 }" class="mr-2">mdi-cancel</v-icon>
                </span>
              </template>
            </v-tooltip>

             <v-tooltip v-if="user.active_patient" :text="$t('basics.activePatient')">
              <template v-slot:activator="{ props }">
                <span v-bind="props">
                  <v-icon color="primary" :style="{ opacity: 1 }" class="mr-2">mdi-star</v-icon>
                </span>
              </template>
            </v-tooltip>
          </td>
          <td>{{ user.last_name }}</td>
          <td>{{ user.first_name }}</td>
          <td v-if="!isMobile">{{ user.email }}</td>
          <td v-if="!isMobile">
            <div v-for="role in user.display_permission">
              {{ $t("constants." + role) }}
            </div>
          </td>
          <td v-if="!isMobile">
            {{
              user.last_login
                ? getReadableDateTime(user.last_login)
                : $t("messages.noLoginFound")
            }}
          </td>
          <td class="text-right">
            <v-tooltip v-if="
              (!user.is_registered || !user.last_login) &&
              user.email &&
              $checkFunctionPermission(['resend_signup_mail'])
            " :text="$t('messages.resendRegistration')">
              <template v-slot:activator="{ props }">
                <span v-bind="props" flat size="small" class="mr-2"
                  @click="showResendTokenWarning($event, user)"><v-icon size="large">mdi-email-outline</v-icon>
                </span>
              </template>
            </v-tooltip>

            <v-tooltip v-if="user.deleted && $checkFunctionPermission(['user_patch_detail'])" :text="$t('messages.unDeleteUser')">
              <template v-slot:activator="{ props }">
                <span v-bind="props" @click="showUndeleteWarning($event, user)">
                  <v-icon :style="{ opacity: 1 }" class="mr-2" size="large">mdi-lock-outline</v-icon>
                </span>
              </template>
            </v-tooltip>

            <v-tooltip :text="$t('basics.edit', { type: $t('basics.user') })">
              <template v-slot:activator="{ props }">
                <span @click="showEditUser($event, user)" v-bind="props">
                  <v-icon size="large">mdi-pencil</v-icon></span>
              </template>
            </v-tooltip>
          </td>
        </tr>
        <tr v-if="!users || users.length == 0">
          <td colspan="10">
            <p>
              <i>{{ $t("messages.noItems") }}</i>
            </p>
          </td>
        </tr>
      </tbody>
    </TableVue>

    <v-snackbar v-model="resendTokenWarning" color="primary" :multi-line="true" :timeout="-1" location="top">
      <b style="font-size: larger"> {{ $t("messages.resendRegistration") }} </b><br /><br />
      <span>{{ $t("messages.resendRegistrationText") }}</span>
      <br />

      <template v-slot:actions>
        <v-btn class="py-3 mb-3" text @click="cancelResendEmailToken()">
          {{ $t("basics.no") }}
        </v-btn>

        <v-btn class="py-3 mb-3" text @click="resendEmailToken()">
          {{ $t("basics.yes") }}
        </v-btn>
      </template>
    </v-snackbar>

    <UserEditVue :refreshDisplay="editUserDrawer" :userData="activeUser"
      :readonly="(!activeUser && !($checkRouterPermission('POST')) || (activeUser && !$checkRouterPermission('PATCH')))" @close="closeEditUser"
      v-if="users.length > 0" />

    <UserDeletionWarningVue :refreshDisplay="userDeletionDrawer" :userData="activeUser"
      @confirm="patchUserDeletionStatus" />

    <LoadingScreenVue :loading="loading" />
  </div>
</template>

<script>
import UserListFilterVue from "../../../components/user/UserListFilter.vue";
import UserEditVue from "../../../components/user/UserEdit.vue";
import UserDeletionWarningVue from "../../../components/main/UserDeletionWarning.vue";

export default {
  name: "userList",
  
  data() {
    return {
      users: [],

      numPages: undefined,
      currPage: 1,
      pageSize: 10,
      totalCount: undefined,
      resetPage: 0,

      sortBy: "last_name",
      sortDirection: true,

      activeUser: undefined,
      editUserDrawer: 0,
      profileDrawer: 0,
      userDeletionDrawer: 0,

      loading: false,

      resendTokenWarning: false,

      searchQueue: "",
      filterQuery: "",
    };
  },

  computed: {
    isMobile() {
      switch (this.$vuetify.display.name) {
        case "xs":
          return true;
        case "sm":
          return true;
        case "md":
          return false;
        case "lg":
          return false;
        case "xl":
          return false;
        case "xxl":
          return false;
        default:
          return false;
      }
    },
    searchBarCols() {
      switch (this.$vuetify.display.name) {
        case "xs":
          return 9;
        case "sm":
          return 9;
        case "md":
          return 6;
        case "lg":
          return 4;
        case "xl":
          return 4;
        case "xxl":
          return 3;
        default:
          return 4;
      }
    },
  },

  components: {
    UserListFilterVue,
    UserEditVue,
    UserDeletionWarningVue
  },

  methods: {
    gotoUser(event, user){
      event.stopPropagation()
      event.preventDefault()

      if(!this.$checkFunctionPermission(["user_detail"])){
        this.showEditUser(event, user)
        return
      }console.log(user.id)

      this.$router.push("/backoffice/users/" + user.id + "/overview")
    },
    gotoUserInNewTab(event, user){
      event.stopPropagation()
      event.preventDefault()

      if(!this.$checkFunctionPermission(["user_detail"])){
        this.showEditUser(event, user)
        return
      }
      
      window.open("/backoffice/users/" + user.id + "/overview")
    },
    showUndeleteWarning(event, user) {
      event.stopPropagation()
      event.preventDefault()

      this.activeUser = user
      this.userDeletionDrawer++
    },
    updateSearchQueue(value) {
      this.resetPage++;
      this.currPage = 1;
      this.searchQueue = value;

      this.fetchData();
    },
    getReadableDate(dateString) {
      if (!dateString) {
        return ""
      }
      return this.$moment(dateString).format("DD.MM.YYYY");
    },
    getReadableDateTime(dateString) {
      return this.$moment(dateString).format("DD.MM.YYYY HH:mm");
    },
    setLoading(value) {
      this.loading = value;
    },
    switchPage(data) {
      this.currPage = data;

      this.fetchData();
    },
    switchSortBy(val) {
      if (this.sortBy != val) {
        this.sortBy = val;
        this.sortDirection = true;
      } else {
        this.sortDirection = !this.sortDirection;
      }

      this.currPage = 1;

      this.fetchData();
    },
    showEditUser(event, user = undefined) {
      if (event) {
        event.stopPropagation();
        event.preventDefault();
      }

      this.activeUser = user;

      this.editUserDrawer++;
    },
    closeEditUser(data) {
      this.editUserDrawer = 0;
      if (data) {
        this.fetchData();
      }
    },
    fetchData() {
      this.setLoading(true);

      let searchParams =
        "?page=" +
        this.currPage +
        "&order=" +
        this.sortBy +
        "&direction=" +
        (this.sortDirection ? "up" : "down");

      if (this.searchQueue) {
        searchParams += "&query=" + this.searchQueue;
      }

      if (this.filterQuery) {
        searchParams += this.filterQuery;
      }

      this.axios
        .get(this.axios.defaults.baseURL + "user/users/" + searchParams)
        .then((resp) => {
          this.users = resp.data.results;
          this.totalCount = resp.data.count;

          if (resp.data.results.length == 0) {
            this.numPages = 0;
          } else if (this.currPage == 1) {
            this.numPages = Math.ceil(resp.data.count / resp.data.results.length);
          }

          this.setLoading(false);
        })
        .catch((e) => {
          console.log(e);
          window.dispatchEvent(new CustomEvent("showErrorDisplay", { detail: e }));
          this.setLoading(false);
        });
    },
    showProfileDrawer(event, user) {
      event.stopPropagation();
      event.preventDefault();

      this.activeUser = user;
      this.profileDrawer++;
    },
    applyFilter(query) {
      this.filterQuery = query;
      this.currPage = 1

      this.fetchData();
    },
    showResendTokenWarning(event, user) {
      event.stopPropagation();
      event.preventDefault();

      this.activeUser = user;

      this.resendTokenWarning = true;
    },
    resendEmailToken() {
      this.axios
        .post(this.axios.defaults.baseURL + "signup/resend/", {
          id: this.activeUser.id,
        })
        .catch((e) => {
          console.log(e);
          window.dispatchEvent(new CustomEvent("showErrorDisplay", { detail: e }));
        });

      this.activeUser = undefined;

      this.resendTokenWarning = false;
    },
    cancelResendEmailToken() {
      this.activeUser = undefined;

      this.resendTokenWarning = false;
    },
    patchUserDeletionStatus() {
      let formData = {
        id: this.activeUser.id,
        deleted: !this.activeUser.deleted
      }

      this.axios
        .patch(this.axios.defaults.baseURL + "user/users/patch/" + formData.id, formData)
        .then((resp) => {
          this.userDeletionDrawer = 0;
          this.activeUser = undefined

          this.fetchData()
        })
        .catch((e) => {
          console.log(e);
          window.dispatchEvent(new CustomEvent("showErrorDisplay", { detail: e }));
        });
    }
  },

  mounted() {
    this.fetchData();
  },
};
</script>
